import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { toast } from 'sonner';
import { Loader2, TrendingUp, Target, PieChartIcon, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#1E3A5F', '#38BDF8', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

export default function AnalyticsPage() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [investments, setInvestments] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, performanceRes, investmentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/overview`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/analytics/performance`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/investments`, { headers: getAuthHeaders() })
      ]);
      setAnalytics(analyticsRes.data);
      setChartData(performanceRes.data.chart_data);
      setInvestments(investmentsRes.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Prepare sector data for pie chart
  const sectorData = analytics?.sectors 
    ? Object.entries(analytics.sectors).map(([name, value]) => ({ name, value }))
    : [];

  // Prepare stage data for bar chart
  const stageData = analytics?.stages
    ? Object.entries(analytics.stages).map(([name, value]) => ({ name, count: value }))
    : [];

  // Top performers
  const topPerformers = [...investments]
    .filter(inv => inv.moic)
    .sort((a, b) => (b.moic || 0) - (a.moic || 0))
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Analytics</h1>
        <p className="text-slate-500 mt-1">Portfolio performance and insights</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Portfolio MOIC</p>
                <p className="font-heading text-2xl font-light text-slate-900">{analytics?.portfolio_moic || 0}x</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Total Invested</p>
                <p className="font-heading text-2xl font-light text-slate-900">
                  {formatCurrency(analytics?.total_invested || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Current Value</p>
                <p className="font-heading text-2xl font-light text-slate-900">
                  {formatCurrency(analytics?.total_current_value || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${analytics?.unrealized_gain >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                <PieChartIcon className={`h-5 w-5 ${analytics?.unrealized_gain >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Unrealized Gain</p>
                <p className={`font-heading text-2xl font-light ${analytics?.unrealized_gain >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(analytics?.unrealized_gain || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Portfolio Growth Chart */}
        <Card data-testid="growth-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Portfolio Growth Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorValue2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1E3A5F" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#1E3A5F" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorInvested2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#38BDF8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} tickFormatter={(value) => formatCurrency(value)} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    name="Current Value"
                    stroke="#1E3A5F" 
                    fillOpacity={1} 
                    fill="url(#colorValue2)" 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="invested" 
                    name="Invested"
                    stroke="#38BDF8" 
                    fillOpacity={1} 
                    fill="url(#colorInvested2)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                <p>No data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sector Distribution */}
        <Card data-testid="sector-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Investment by Sector</CardTitle>
          </CardHeader>
          <CardContent>
            {sectorData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={sectorData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {sectorData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                <p>No data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stage Distribution */}
        <Card data-testid="stage-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Investment by Stage</CardTitle>
          </CardHeader>
          <CardContent>
            {stageData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stageData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Investments" fill="#1E3A5F" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                <p>No data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Performers */}
        <Card data-testid="top-performers">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Top Performers by MOIC</CardTitle>
          </CardHeader>
          <CardContent>
            {topPerformers.length > 0 ? (
              <div className="space-y-4">
                {topPerformers.map((inv, index) => (
                  <div key={inv.id} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-slate-900">{inv.company_name}</p>
                        <p className="text-xs text-slate-400">{inv.sector}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-mono font-semibold ${inv.moic >= 1 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {inv.moic}x
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatCurrency(inv.investment_amount)} invested
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-slate-400">
                <p>No investments with valuation data</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Summary */}
      {analytics?.pipeline_stats && Object.keys(analytics.pipeline_stats).length > 0 && (
        <Card data-testid="pipeline-summary">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Deal Pipeline Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(analytics.pipeline_stats).map(([stage, count]) => (
                <div key={stage} className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-heading font-light text-slate-900">{count}</p>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                    {stage.replace('_', ' ')}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
